import { MantineProvider } from '@mantine/core';
import _sum from 'lodash/sum';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';

import type { EstimationModelResponse } from '@/api/schema/data-contracts';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { useMessages } from '@/hooks/messages';
import type {
  CostModel,
  ResourceEstimationItemsTableRecord,
} from '@/pages/estimation/detail/model';
import { sortCostTypes } from '@/utils/sorting';

import { estimationItemsTableMessageDefinitions } from './messages';

interface Row {
  id: '';
}

export interface SummaryTableProps {
  estimationItems: ResourceEstimationItemsTableRecord[];
  costs?: CostModel;
  estimationModel: EstimationModelResponse;
  showCosts?: boolean;
  isManagementReportTab?: boolean;
  showEstimateColumn?: boolean;
  hiddenCostTypeColumns?: string[];
}

const SummaryTable: React.FC<SummaryTableProps> = ({
  estimationItems = [],
  estimationModel,
  costs,
  showCosts,
  isManagementReportTab = false,
  showEstimateColumn = false,
  hiddenCostTypeColumns = [],
}: SummaryTableProps) => {
  const messages = useMessages(estimationItemsTableMessageDefinitions);

  const costsColumns: DataTableColumn<Row>[] = sortCostTypes(
    estimationModel.costTypes,
    estimationModel.costTypeGroups,
  )
    .filter((costType) => !hiddenCostTypeColumns.includes(costType.id))
    .map((costType) => {
      return {
        accessor: 'costType' + costType.id,
        title: costType.name,
        noWrap: true,
        width: 200,
        textAlign: 'right',
        hidden: !costs || !showCosts,
        titleClassName: 'whitespace-break-spaces',
        render: () => {
          if (!costs) {
            return undefined;
          }

          const calculatedCost = _sum(
            estimationItems
              .filter((estimationItem) => !estimationItem.isOmitted)
              .map((estimationItem) => {
                const resourcesForCostType =
                  estimationItem.resources?.filter((resource) =>
                    costs.resourceTypeCosts.find(
                      (resourceTypeCost) =>
                        resourceTypeCost.costTypeId === costType.id &&
                        resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                    ),
                  ) ?? [];
                const estimationItemCost = _sum(
                  resourcesForCostType.map((resource) => {
                    const resourceTypeCost = costs.resourceTypeCosts.find(
                      (rtc) =>
                        rtc.costTypeId === costType.id &&
                        rtc.resourceTypeId === resource.resourceTypeId,
                    );
                    return (resource.value ?? 0) * (resourceTypeCost?.price ?? 0);
                  }),
                );
                return estimationItemCost;
              }),
          );
          return <FormattedNumber value={calculatedCost} />;
        },
      };
    });

  const columns: DataTableColumn<Row>[] = [
    {
      accessor: 'hierarchicalOrderReference_placeholder',
      title: '',
      sortable: false,
      width: 50,
      render: () => null,
    },
    {
      accessor: 'name_placeholder',
      title: '',
      sortable: false,
      width: 300,
      render: () => null,
    },
    {
      accessor: 'buttons_placeholder',
      title: '',
      width: 150,
      render: () => null,
    },
    {
      accessor: 'responsible_placeholder',
      title: '',
      width: 200,
      hidden: !showEstimateColumn,
      render: () => null,
    },
    {
      accessor: 'method_placeholder',
      title: '',
      width: 180,
      hidden: !showEstimateColumn,
      render: () => null,
    },
    {
      accessor: 'estimate',
      title: showEstimateColumn
        ? messages.estimate()
        : isManagementReportTab
          ? messages.totalEfforts()
          : '',
      noWrap: true,
      textAlign: 'right',
      width: showEstimateColumn ? 150 : isManagementReportTab ? 180 : 150,
      render: () =>
        showEstimateColumn ? (
          <FormattedNumber
            value={_sum(estimationItems.filter((x) => !x.isOmitted).map((x) => x.calculatedResult))}
          />
        ) : null,
    },
    ...costsColumns,
    {
      accessor: 'subTotal',
      title: messages.totalSum(),
      noWrap: true,
      width: 200,
      textAlign: 'right',
      hidden: !costs,
      titleClassName: 'whitespace-break-spaces',
      render: () => {
        if (!costs) {
          return undefined;
        }

        const calculatedCost = _sum(
          estimationItems
            .filter((estimationItem) => !estimationItem.isOmitted)
            .map((estimationItem) =>
              _sum(
                (estimationItem.resources ?? []).map((resource) => {
                  const resourceTypeCost = costs.resourceTypeCosts.find(
                    (rtc) => rtc.resourceTypeId === resource.resourceTypeId,
                  );
                  return (resource.value ?? 0) * (resourceTypeCost?.price ?? 0);
                }),
              ),
            ),
        );
        return <FormattedNumber value={calculatedCost} />;
      },
    },

    ...(isManagementReportTab
      ? [
          {
            accessor: 'assumptions_placeholder',
            title: '',
            width: 300,
            render: () => null,
          },
        ]
      : []),
  ];

  return (
    <div className='mt-4'>
      <MantineProvider>
        <div className='relative'>
          <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
          <DataTable<Row>
            highlightOnHover
            withTableBorder
            className='table-hover rounded-xl p-2 whitespace-nowrap'
            records={[
              {
                id: '' /* id is needed just to prevent react warning about missing key property */,
              },
            ]}
            columns={columns}
          />
        </div>
      </MantineProvider>
    </div>
  );
};

export default SummaryTable;
